import React, { useEffect, useMemo, useState } from 'react';
import MemberAuthGate from '../auth/MemberAuthGate';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import { isNativeShell } from '../auth/authRedirect';
import { callGearHandoff, handoffTypeLabel } from './gearHandoffApi';
import { WorkflowAlert, WorkflowComplete } from './WorkflowFeedback';
import './tracking.css';
import './my-gear.css';

const APP_STORE_URL = 'https://apps.apple.com/app/line-medic/id6806487617';

function HandoffAccept({ member, token }) {
  const [session, setSession] = useState(null);
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    callGearHandoff('preview', { token })
      .then(payload => {
        if (cancelled) return;
        const next = payload?.session || null;
        setSession(next);
        setSelected([]);
        setStatus('ready');
      })
      .catch(previewError => {
        if (cancelled) return;
        setError(previewError?.message || 'Unable to open this gear handoff.');
        setStatus('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  const eligibleAssets = useMemo(
    () => (session?.assets || []).filter(asset => asset.eligible && !asset.returned),
    [session],
  );

  const toggle = id => setSelected(current => current.includes(id)
    ? current.filter(item => item !== id)
    : [...current, id]);

  const accept = async () => {
    setError('');
    if (!selected.length) return setError('Choose at least one physical asset to accept.');
    setStatus('working');
    try {
      const payload = await callGearHandoff('accept', { token, assetIds: selected });
      const accepted = payload?.session || session;
      setSession(accepted);
      setComplete({
        type: accepted?.handoffType || session?.handoffType,
        count: accepted?.acceptedAssetIds?.length || selected.length,
        outgoingName: accepted?.outgoingName || session?.outgoingName,
        incident: accepted?.incident || session?.incident,
      });
      setStatus('ready');
    } catch (acceptError) {
      setError(acceptError?.message || 'Unable to accept the gear handoff.');
      setStatus('ready');
    }
  };

  if (complete) {
    const returnCustody = complete.type === 'return_on_behalf';
    return (
      <div className="tracking-root">
        <main className="tracking-page">
          <WorkflowComplete
            eyebrow={returnCustody ? 'Return custody accepted' : 'Gear handoff accepted'}
            title={returnCustody ? 'You are returning this gear for them' : 'The gear is now in your My Gear'}
            summary={returnCustody
              ? `The operational assignment stays with ${complete.outgoingName}. You are recorded as the physical return custodian until the gear is checked back into the cache.`
              : `The selected gear moved from ${complete.outgoingName} to your incident assignment.`}
            details={[
              { label: 'Incident', value: complete.incident || 'Assignment' },
              { label: 'Physical gear', value: `${complete.count} assets` },
              { label: 'Handoff', value: handoffTypeLabel(complete.type) },
            ]}
            primaryLabel="Open My Gear"
            onPrimary={() => window.location.assign('/my-gear')}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="tracking-root">
      <header className="tracking-topbar">
        <div>
          <h1>Gear Handoff</h1>
          <p>{member?.full_name || 'Member'} · secure individual account</p>
        </div>
        <div className="tracking-member-actions">
          <button type="button" className="tracking-signout" onClick={() => window.location.assign('/my-gear')}>My Gear</button>
        </div>
      </header>
      <main className="tracking-page">
        <section className="tracking-card">
          {status === 'loading' && <div className="tracking-empty">Checking the handoff…</div>}

          {session && (
            <>
              <div className="tracking-section-head">
                <div>
                  <span className="my-gear-eyebrow">FROM {String(session.outgoingName || 'MEMBER').toUpperCase()}</span>
                  <h2>{handoffTypeLabel(session.handoffType)}</h2>
                  <p>{session.incident}{session.incidentOrderNumber ? ` · ${session.incidentOrderNumber}` : ''}</p>
                </div>
              </div>

              <div className="tracking-identity-note">
                {session.handoffType === 'return_on_behalf'
                  ? <><strong>You are not taking over the deployment.</strong> Accept only the gear physically changing hands. It will appear in My Gear as <strong>Pending Return</strong> until you return it to the cache.</>
                  : <><strong>You are assuming responsibility for the selected gear.</strong> Line Medic will continue/create your matching incident assignment and remove those assets from the outgoing member.</>}
              </div>

              {['accepted', 'completed'].includes(session.status) ? (
                <div className="tracking-empty">
                  This handoff has already been accepted.
                  <div style={{ marginTop: 12 }}>
                    <button type="button" className="tracking-btn tracking-btn-primary" onClick={() => window.location.assign('/my-gear')}>Open My Gear</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tracking-gear-block" style={{ marginTop: 16 }}>
                    <div className="tracking-section-head">
                      <div><h3>Physical gear</h3><p>Select only what you actually received.</p></div>
                      <button type="button" className="tracking-link-btn" onClick={() => setSelected(eligibleAssets.map(asset => asset.id))}>Select all</button>
                    </div>
                    <div className="tracking-asset-chips">
                      {(session.assets || []).map(asset => {
                        const chosen = selected.includes(asset.id);
                        return (
                          <button
                            type="button"
                            key={asset.id}
                            disabled={!asset.eligible}
                            className={`tracking-asset-chip${chosen ? ' selected' : ''}`}
                            onClick={() => asset.eligible && toggle(asset.id)}
                          >
                            <strong>{asset.name || asset.id}</strong>
                            <small>{asset.id}{!asset.eligible ? ' · no longer eligible' : ''}</small>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button type="button" className="tracking-btn tracking-btn-primary" disabled={status === 'working' || !selected.length} onClick={accept}>
                    {status === 'working'
                      ? 'Recording handoff…'
                      : session.handoffType === 'return_on_behalf'
                        ? `Accept ${selected.length} for Return`
                        : `Take Over ${selected.length} Assets`}
                  </button>
                </>
              )}
            </>
          )}

          <WorkflowAlert message={error} title="Handoff couldn't continue" onClose={() => setError('')} />
        </section>
      </main>
    </div>
  );
}

export default function GearHandoffRoute({ token }) {
  const native = isNativeShell();
  const [continueWeb, setContinueWeb] = useState(native);

  if (!native && !continueWeb) {
    return (
      <div className="member-auth-root">
        <section className="member-auth-card">
          <img className="member-auth-mark" src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />
          <h1>Line Medic Gear Handoff</h1>
          <p>A Line Medic or REMS member sent you a secure, one-time gear handoff.</p>
          <div className="member-auth-note">
            If Line Medic is installed, reopen this handoff link from the iPhone. If you need the app, install it below, then return to this same link before it expires.
          </div>
          <a className="member-auth-primary" href={APP_STORE_URL} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>Get Line Medic</a>
          <button type="button" className="member-auth-link" onClick={() => setContinueWeb(true)}>Continue / Sign In Here</button>
        </section>
      </div>
    );
  }

  return (
    <MemberAuthGate>
      {profile => <HandoffAccept member={profile} token={token} />}
    </MemberAuthGate>
  );
}
